import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Starfield from "@/components/Starfield";
import { ArrivalWarp } from "@/components/ArrivalWarp";
import BootSequence from "@/components/BootSequence";
import { LandingVoiceGreeter } from "@/components/LandingVoiceGreeter";
import { DownloadModal } from "@/components/DownloadModal";
import { landingLaunchBridge, voyageStatusBridge, bayTourBridge, voiceDisconnectBridge, voiceHandoffBridge } from "@/lib/voiceLaunchBridge";
import { Smartphone } from "lucide-react";

const CATEGORY_COLORS = {
  health: "#FF3366", security: "#00F0FF", drone: "#FF9F1C", finance: "#10B981",
  weather: "#8B5CF6", culture: "#FF9F1C", "neural-ops": "#00F0FF", environment: "#10B981",
  identity: "#FF3366", vision: "#8B5CF6", sports: "#FF9F1C", infrastructure: "#94A3B8",
  energy: "#FF3366", diplomacy: "#38BDF8", general: "#94A3B8",
};

// A docked project's live-sync health: if AVA's background research/git-sync loop hasn't
// touched it in over 7 days (or never has), its dock socket flickers amber as a quiet
// "hasn't heard from this ship in a while" signal.
const STALE_SYNC_MS = 7 * 24 * 60 * 60 * 1000;
function isStaleSync(project) {
  if (!project.last_synced_at) return true;
  return Date.now() - new Date(project.last_synced_at).getTime() > STALE_SYNC_MS;
}

// Cache-busting build tag - bump this whenever ship/mothership art changes so
// returning browsers can never serve a stale cached asset from a prior session.
const ASSET_VERSION = "20260826a";
const EMBLEM_URL = `https://static.prod-images.emergentagent.com/jobs/ad6f2b54-5f29-4014-ae49-25799dca8ff0/images/5a8da31a9477e0eebb9616f2297b2f333663116c58c795b5dca462667ae76d19.jpeg?v=${ASSET_VERSION}`;
const MOTHERSHIP_URL = `/mothership_manta.jpg?v=${ASSET_VERSION}`;
// One unified stealth-delta fleet design (Doc's hand-picked hull), tinted per squadron so
// every ship reads as part of ONE navy while staying identifiable by category at a glance.
const FLEET_CATEGORIES = [
  "health", "security", "drone", "finance", "weather", "culture", "neural-ops", "environment",
  "identity", "vision", "sports", "infrastructure", "energy", "diplomacy", "general",
];
const fleetShipSrc = (category) =>
  `/ships/fleet_${FLEET_CATEGORIES.includes(category) ? category : "general"}.png?v=${ASSET_VERSION}`;

const ARCHITECTURE_LABELS = [
  { top: 22, left: 18, label: "HIPPOCAMPUS CORE", sub: "Fast Hebbian memory" },
  { top: 22, left: 82, label: "NEOCORTEX ARRAY", sub: "Deep reasoning layer" },
  { top: 88, left: 24, label: "BAYESIAN GRID", sub: "Confidence propagation" },
  { top: 88, left: 78, label: "CNN / ANN DEPLOY BAY", sub: "Mission-adaptive nets" },
];

// Default positions for the landing page's draggable text elements (the "AVA" heading +
// the 4 architecture labels above). Same Drag-to-Dock idea as the spacecraft: admin drags
// each one into place in Edit Layout mode, then "Lock Positions" persists it via
// /api/layout/text/lock so every visitor sees the calibrated placement going forward.
const TEXT_ELEMENT_DEFAULTS = {
  "ava-heading": { x: 48.5, y: 12.5 },
  "label-0": { x: ARCHITECTURE_LABELS[0].left, y: ARCHITECTURE_LABELS[0].top },
  "label-1": { x: ARCHITECTURE_LABELS[1].left, y: ARCHITECTURE_LABELS[1].top },
  "label-2": { x: ARCHITECTURE_LABELS[2].left, y: ARCHITECTURE_LABELS[2].top },
  "label-3": { x: ARCHITECTURE_LABELS[3].left, y: ARCHITECTURE_LABELS[3].top },
};

// Orbital Docking Ring: bays are no longer traced from pockets baked into the art - the
// app renders its OWN luminous ring of landing pads on a perfect ellipse around the
// carrier, so every ship seats pixel-perfectly by construction, and the ring simply
// redistributes itself as the fleet grows (unlimited bays, always spare capacity shown).
// Admin drag-locks from "Edit Layout" still override any auto slot per-project.
const MIN_BAYS = 38;
const RING_CX = 50;
const RING_CY = 45;
const RING_RX = 41;
const RING_RY = 33;
// The command dome - where the hull-glow (carrier illuminated from inside) breathes from.
const DOME_CX = 50;
const DOME_CY = 30;
const SHIP_BASE_PX = 76;
// Ship art was calibrated against the mothership container's own max-w-[1500px] design
// reference. Expressing ship size as a % of that same reference (rather than a flat px
// value) keeps every ship correctly scaled to its bay pocket at ANY screen width - a fixed
// px ship looked fine on a ~1500px desktop container but was 4x too large relative to a
// ~340px iPhone container, causing the whole fleet to pile up into a jumbled overlapping
// mess on mobile even though its x/y % anchor points were always correct.
const MOTHERSHIP_REFERENCE_PX = 1500;
const SHIP_BASE_PCT = (SHIP_BASE_PX / MOTHERSHIP_REFERENCE_PX) * 100;
const CONTAINER_ASPECT = 1.5;
// Touch/mobile devices (iOS Safari especially) show camera+mic permission prompts as
// native, modal, blocking dialogs - unlike desktop's small non-blocking address-bar
// prompt. LandingVoiceGreeter firing 2 of these on page load could eat the very tap
// meant to open the login modal underneath, locking a mobile visitor out entirely. It's
// a "nice-to-have" ambient touch, never worth risking that - skip it on coarse pointers.
const IS_TOUCH_DEVICE = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

// Final parking scale after the cinematic arrival - the carrier settles noticeably
// further out than full-frame ("too much in the face"), then holds there permanently.
const ARRIVAL_FINAL_SCALE = 0.8;
const ARRIVAL_START_SCALE = 0.012;
const ARRIVAL_MS = 6000;
// Reverse-Star-Wars approach easing: a ship flying AT the camera at near-constant speed
// grows exponentially on screen (perceived size ~ 1/distance) - almost still while deep
// out, swelling faster and faster as it closes, then a gentle thruster deceleration into
// park. One continuous curve = zero keyframe seams, so no staggered "hiccup" zoom.
const approachEase = (t) => {
  const u = 1 - Math.pow(1 - t, 1.5);
  const s = ARRIVAL_START_SCALE * Math.pow(ARRIVAL_FINAL_SCALE / ARRIVAL_START_SCALE, u);
  return (s - ARRIVAL_START_SCALE) / (ARRIVAL_FINAL_SCALE - ARRIVAL_START_SCALE);
};

// Evenly distributes `count` docking slots around the ring ellipse, starting at 12
// o'clock and going clockwise. Facing turns every ship nose-in toward the carrier core
// (aspect-corrected - the container is 3:2, so screen-space angles need dx * 1.5), and
// depthScale renders the near (bottom) arc larger than the far (top) arc for real depth.
function generateRingSlots(count) {
  const slots = [];
  for (let i = 0; i < count; i++) {
    const t = -Math.PI / 2 + (i / count) * Math.PI * 2;
    const x = RING_CX + RING_RX * Math.cos(t);
    const y = RING_CY + RING_RY * Math.sin(t);
    const dxScreen = (x - RING_CX) * CONTAINER_ASPECT;
    const dyScreen = y - RING_CY;
    const outward = (Math.atan2(dxScreen, -dyScreen) * 180) / Math.PI;
    const facing = (outward + 180 + 360) % 360;
    const depthScale = 0.68 + ((Math.sin(t) + 1) / 2) * 0.52;
    slots.push({ x, y, facing, depthScale });
  }
  return slots;
}

// Doc's wing-bay spec: 10 illuminated rectangular bays per wing, in the CARRIER'S OWN
// frame (it faces the viewer, so its LEFT wing is the image's bottom-right arc and its
// RIGHT wing is the image-left column). 18 ships are locked server-side into bays 1-9 of
// each wing; each wing's 10th bay stays vacant with a blinking beacon - these are the
// ready pads any newly docked (unlocked) project seats into first.
const VACANT_BAYS = [
  { x: 81.9, y: 69.5, facing: 298.05, depthScale: 1.2145 },
  { x: 74.4, y: 75.1, facing: 310.36, depthScale: 1.2451 },
  { x: 18.29, y: 69.02, facing: 62.25, depthScale: 1.2118 },
  { x: 25.6, y: 74.9, facing: 49.83, depthScale: 1.244 },
  // Inner-circle stations (Doc's expansion): two per side on the deck just inside
  // the ring's inner edge, clear of the central spine and painted deck craft.
  { x: 30.5, y: 44.0, facing: 55.0, depthScale: 1.03 },
  { x: 32.5, y: 59.5, facing: 51.0, depthScale: 1.13 },
  { x: 69.5, y: 44.0, facing: 305.0, depthScale: 1.03 },
  { x: 67.5, y: 59.5, facing: 309.0, depthScale: 1.13 },
  // Outer saucer-skirt berths (Doc's second expansion, 2026-08): the hull art now
  // extends into a wider outer deck ring - these four pads sit on its port/starboard
  // flanks, bringing total fleet capacity to exactly 30. The art's extra carved
  // pockets beyond these read as reserve capacity. `outer` marks them for the
  // radar-ping beacon shimmer so spare capacity reads at a glance.
  { x: 9.5, y: 57.5, facing: 78.4, depthScale: 1.06, outer: true },
  { x: 12.5, y: 66.5, facing: 69.1, depthScale: 1.12, outer: true },
  { x: 90.5, y: 57.5, facing: 281.6, depthScale: 1.06, outer: true },
  { x: 87.5, y: 66.5, facing: 290.9, depthScale: 1.12, outer: true },
  // Reserve rim berths (2026-08-27): the original 4 outer pads are already hosting
  // VAR / Zerowater / ID Shield / Quran, so these four freshly-commissioned berths on
  // the saucer-skirt rim carry the radar-ping beacon - genuine spare capacity that
  // reads at a glance. Total fleet capacity: 34 pads.
  { x: 6.0, y: 61.0, facing: 80.0, depthScale: 1.05, outer: true },
  { x: 9.0, y: 71.0, facing: 70.0, depthScale: 1.1, outer: true },
  { x: 94.0, y: 61.0, facing: 280.0, depthScale: 1.05, outer: true },
  { x: 91.0, y: 71.0, facing: 290.0, depthScale: 1.1, outer: true },
];
// Total berth capacity: the 18 hand-locked wing bays + every pad in VACANT_BAYS.
// Single source of truth - the Docking Bay's capacity gauge imports this.
export const FLEET_CAPACITY = 18 + VACANT_BAYS.length;

function getShipLayout(project, slotOrdinal, total, freeBays = VACANT_BAYS) {
  const color = CATEGORY_COLORS[project.category] || CATEGORY_COLORS.general;
  const shipImg = fleetShipSrc(project.category);
  // A human-calibrated dock position always wins over the auto-computed slot -
  // this is what "Lock Positions" (drag-to-dock mode) writes to the project document.
  if (project.dock_x != null && project.dock_y != null) {
    return {
      x: project.dock_x,
      y: project.dock_y,
      color,
      facing: project.dock_facing ?? 0,
      depthScale: project.dock_depth_scale ?? 1,
      shipImg,
    };
  }
  // Unlocked ships fill GENUINELY-free wing bays first (pads already claimed by a
  // locked ship are skipped - a new arrival must never seat on top of a docked ship,
  // which is exactly how OCTON Virology once landed over OCTON Retina), then overflow
  // onto the ring.
  if (slotOrdinal < freeBays.length) {
    const pad = freeBays[slotOrdinal];
    return { x: pad.x, y: pad.y, color, facing: pad.facing, depthScale: pad.depthScale, shipImg };
  }
  const slots = generateRingSlots(Math.max(MIN_BAYS, total));
  const slot = slots[slotOrdinal % slots.length];
  return { x: slot.x, y: slot.y, color, facing: slot.facing, depthScale: slot.depthScale, shipImg };
}

function ShipConnector({ x, y, color }) {
  return (
    <>
      <line x1={`${RING_CX}%`} y1={`${RING_CY}%`} x2={`${x}%`} y2={`${y}%`} stroke={color} strokeOpacity="0.22" strokeWidth="1" className="ion-in" />
      <line x1={`${RING_CX}%`} y1={`${RING_CY}%`} x2={`${x}%`} y2={`${y}%`} stroke={color} strokeOpacity="0.14" strokeWidth="1" className="ion-out" />
    </>
  );
}

// A single luminous landing pad on the orbital docking ring - a pure soft light pool,
// no visible outlines (Doc's ask: clean cinematic hull, no UI markup on the ship).
// Occupied pads breathe in their squadron's colour beneath the seated ship; vacant pads
// pulse a very faint cyan "ready" glow - quiet spare capacity around the carrier.
function BayPad({ x, y, depthScale, color, occupied, index, outer }) {
  const wPct = SHIP_BASE_PCT * depthScale * 1.6;
  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, width: `${wPct}%`, aspectRatio: "2 / 1", transform: "translate(-50%, -50%)" }}
      data-testid={`bay-pad-${index}`}
    >
      <motion.span
        className="absolute inset-0 rounded-[50%]"
        style={{ background: `radial-gradient(ellipse at center, ${color}3d 0%, ${color}14 45%, transparent 72%)`, filter: "blur(4px)" }}
        animate={{ opacity: occupied ? [0.5, 0.9, 0.5] : [0.4, 0.75, 0.4] }}
        transition={{ duration: occupied ? 2.4 : 3.6, repeat: Infinity, ease: "easeInOut", delay: (index % 6) * 0.4 }}
      />
      {!occupied && (
        <motion.span
          className="absolute left-1/2 top-1/2 w-[7%] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}, 0 0 20px ${color}66` }}
          animate={{ opacity: [0.25, 0.95, 0.25] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: (index % 5) * 0.3 }}
        />
      )}
      {/* Outer-berth beacon: a slow radar-ping ring sweeping outward from the pad so
          the 4 spare saucer-skirt berths read as ready capacity at a glance. */}
      {!occupied && outer && (
        <motion.span
          data-testid={`outer-beacon-${index}`}
          className="absolute rounded-[50%] border border-cyan-300/60 pointer-events-none"
          style={{ left: "15%", top: "15%", width: "70%", height: "70%", boxShadow: "0 0 14px rgba(103,232,249,0.35)" }}
          initial={{ scale: 0.35, opacity: 0 }}
          animate={{ scale: [0.35, 1.35], opacity: [0.55, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut", delay: (index % 4) * 0.7 }}
        />
      )}
    </div>
  );
}

// A clean, synthesized whoosh (filtered noise sweep + a soft low thump - no voice, no
// countdown, no engine-roar rumble). Pre-fetched and pre-DECODED once at page load into a
// Web Audio buffer so playback starts the very instant a ship is clicked - the old
// fresh-Audio()-per-launch approach had to download + decode the mp3 first, which is why
// the sound used to arrive noticeably after the liftoff had already begun.
const WHOOSH_SOUND_URL = `/sounds/launch_whoosh.mp3?v=${ASSET_VERSION}`;
let whooshCtx = null;
let whooshBuffer = null;
(async () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    whooshCtx = new AudioContextClass();
    const res = await fetch(WHOOSH_SOUND_URL);
    whooshBuffer = await whooshCtx.decodeAudioData(await res.arrayBuffer());
  } catch (e) {
    whooshCtx = null; // Web Audio unavailable - playWhooshSound falls back to <audio>
  }
})();
function playWhooshSound() {
  try {
    if (whooshCtx && whooshBuffer) {
      if (whooshCtx.state === "suspended") whooshCtx.resume();
      const src = whooshCtx.createBufferSource();
      src.buffer = whooshBuffer;
      const gain = whooshCtx.createGain();
      gain.gain.value = 0.85;
      src.connect(gain);
      gain.connect(whooshCtx.destination);
      src.start(0); // zero-latency: buffer already decoded, overlapping launches mix fine
      return;
    }
    const audio = new Audio(WHOOSH_SOUND_URL);
    audio.volume = 0.85;
    audio.play().catch(() => {
      // Autoplay/user-gesture policy blocked it - fail silently, the launch animation still plays.
    });
  } catch (e) {
    // Audio unavailable - fail silently.
  }
}

// Arrival engine: the whoosh buffer looped for the WHOLE approach with a gain envelope
// that mirrors the flight - near-silent while she's deep out, swelling as she closes,
// released exactly at touchdown. Never cut before she's parked out front.
function playArrivalEngine(durationMs) {
  try {
    if (!whooshCtx || !whooshBuffer) { playWhooshSound(); return; }
    if (whooshCtx.state === "suspended") whooshCtx.resume();
    const now = whooshCtx.currentTime;
    const dur = durationMs / 1000;
    const src = whooshCtx.createBufferSource();
    src.buffer = whooshBuffer;
    src.loop = true;
    const gain = whooshCtx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + dur * 0.35);
    gain.gain.exponentialRampToValueAtTime(0.9, now + dur * 0.8);
    gain.gain.setValueAtTime(0.9, now + dur * 0.9);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(gain);
    gain.connect(whooshCtx.destination);
    src.start(now);
    src.stop(now + dur + 0.05);
  } catch (e) {
    // Audio unavailable - the flight still plays silently.
  }
}

// Soft two-tone docking chime, synthesized on the fly - no audio asset needed.
function playDockingChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[660, 0], [990, 0.18]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + delay + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.9);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 1);
    });
    setTimeout(() => ctx.close(), 1600);
  } catch { /* audio blocked - silent docking */ }
}

function ShipMarker({ project, index, x, y, color, facing, depthScale, shipImg, onLaunch, editMode, onDragStart, onRotateStart, isDragging, launching, landing, health }) {
  const size = Math.round(SHIP_BASE_PX * depthScale); // px approximation, used only for the edit-mode rotate-handle offset
  const sizePct = SHIP_BASE_PCT * depthScale; // actual rendered size - a % of the mothership container's width, so it scales correctly on any screen
  const handleRadius = size / 2 + 16;
  // A realistic liftoff: the ship straightens nose-up (0deg, matching the art's own default
  // orientation) and ascends straight up off the pad into the sky - never sideways, that
  // isn't how rockets actually launch.
  const straightFacing = 0;
  return (
    <motion.button
      data-testid={`ship-orbiter-${project.id}`}
      onPointerDown={editMode ? (e) => onDragStart(e, project) : undefined}
      onClick={editMode ? (e) => e.stopPropagation() : (e) => { e.stopPropagation(); onLaunch(project, { x, y, color, facing }); }}
      title={editMode ? `Drag ${project.name} into its bay` : `Launch ${project.name}`}
      className={`group absolute ${editMode ? "cursor-grab active:cursor-grabbing touch-none" : ""}`}
      style={{
        left: `${x}%`, top: `${y}%`,
        width: `${sizePct}%`, aspectRatio: "1 / 1",
        // Center-anchor via negative % margins (relative to container width, box is square).
        // A CSS translate(-50%,-50%) here gets WIPED by framer-motion's animated transform
        // (scale/y), which silently re-anchored every ship to its top-left corner and pushed
        // it off its bay's illuminated rectangle - margins are immune to that override.
        marginLeft: `-${sizePct / 2}%`, marginTop: `-${sizePct / 2}%`,
        zIndex: isDragging || launching ? 30 : undefined, pointerEvents: "auto",
      }}
      initial={landing ? { opacity: 0, scale: 1.3, y: -120 } : { opacity: 0, scale: 0 }}
      animate={launching ? { y: -1100, opacity: 0, scale: 0.4 } : { opacity: 1, scale: 1, y: 0 }}
      transition={launching
        ? { duration: 1.05, ease: "easeIn" }
        : landing
          ? { type: "spring", stiffness: 70, damping: 13 }
          : { delay: 0.15 * (index % 6), duration: 0.5 }}
    >
      {/* Docking ceremony - an expanding touchdown ring the moment the ship settles home. */}
      {landing && (
        <motion.span
          className="absolute inset-[-45%] rounded-full border-2 border-cyan-300/60 pointer-events-none"
          initial={{ scale: 0.35, opacity: 0.9 }}
          animate={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          data-testid={`docking-ring-${project.id}`}
        />
      )}
      {editMode && (
        <>
          <motion.span
            className="absolute inset-0 rounded-full border border-dashed"
            style={{ borderColor: isDragging ? "#FFD23F" : "rgba(0,240,255,0.5)", margin: -10 }}
            animate={{ scale: isDragging ? [1, 1.08, 1] : [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <div
            className="absolute left-1/2 top-1/2 pointer-events-none"
            style={{ width: 0, height: 0, transform: `rotate(${facing}deg)` }}
          >
            <div
              data-testid={`rotate-handle-${project.id}`}
              onPointerDown={(e) => onRotateStart(e, project)}
              title="Drag to rotate this ship's nose direction"
              role="button"
              tabIndex={0}
              className="absolute pointer-events-auto rounded-full flex items-center justify-center touch-none"
              style={{
                left: 0,
                top: -handleRadius,
                width: 18,
                height: 18,
                transform: "translate(-50%, -50%)",
                background: "#FFD23F",
                boxShadow: "0 0 8px rgba(255,210,63,0.9), 0 0 2px rgba(0,0,0,0.6)",
                cursor: "grab",
              }}
            >
              <span className="w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-b-[5px] border-b-black/70" />
            </div>
          </div>
        </>
      )}
      {/* Bay under-glow - the lit pocket's ice-teal light catching the hull from below,
          grounding the ship into the scene instead of floating sticker-like above it. */}
      <motion.span
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(ellipse at center, ${color}38 0%, rgba(0,240,255,0.14) 42%, transparent 68%)`, filter: "blur(5px)" }}
        animate={launching ? { opacity: 1, scale: 1.5 } : { opacity: [0.45, 0.85, 0.45], scale: 1.35 }}
        transition={launching ? { duration: 0.25 } : { duration: 2.6 + (index % 3) * 0.5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="relative w-full h-full transition-transform duration-300 group-hover:scale-[1.5] group-hover:z-10"
        style={{
          rotate: launching ? undefined : facing,
          filter: "drop-shadow(0 3px 7px rgba(0,0,0,0.95)) drop-shadow(0 0 12px rgba(0,240,255,0.3)) brightness(1.04) contrast(1.08)",
        }}
        animate={launching ? { rotate: straightFacing, scale: 1.05 } : { scale: [1, 1.05, 1] }}
        transition={
          launching
            ? { duration: 0.2, ease: "easeOut" }
            : { duration: 3.5 + (index % 4), repeat: Infinity, ease: "easeInOut" }
        }
      >
        <img
          src={shipImg}
          alt={project.name}
          className="w-full h-full object-contain transition-[filter] duration-300 group-hover:brightness-125"
          draggable={false}
        />
        {/* Idle engine plasma over the rear nozzles (art is nose-up, engines at bottom) -
            breathes softly while docked, flares hard the instant a launch begins. */}
        <motion.span
          className="absolute pointer-events-none rounded-full"
          style={{
            left: "32%", width: "36%", bottom: "-1%", height: "16%",
            background: `radial-gradient(ellipse at center, rgba(255,255,255,0.95) 0%, ${color}66 50%, transparent 78%)`,
            filter: "blur(2.5px)",
          }}
          animate={launching || landing ? { opacity: 1, scaleY: 2.8, scaleX: 1.35 } : { opacity: [0.25, 0.65, 0.25], scaleY: [1, 1.2, 1] }}
          transition={launching || landing ? { duration: 0.25 } : { duration: 1.5 + (index % 4) * 0.35, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      {/* Live health pulse - emerald steady = app up, rose flash = down. */}
      {health && health !== "unknown" && !editMode && (
        <span
          data-testid={`ship-health-${project.id}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 7, height: 7, top: "10%", right: "12%",
            backgroundColor: health === "up" ? "#34d399" : "#fb7185",
            boxShadow: health === "up" ? "0 0 8px rgba(52,211,153,0.9)" : "0 0 10px rgba(251,113,133,1)",
            animation: health === "up" ? "pulse 3s ease-in-out infinite" : "pulse 0.8s ease-in-out infinite",
          }}
          title={health === "up" ? `${project.name} is live` : `${project.name} looks DOWN`}
        />
      )}
      <span
        data-testid={`ship-label-${project.id}`}
        className={`absolute left-1/2 top-full -translate-x-1/2 mt-1 text-[9px] font-mono-data uppercase tracking-wider text-white transition-opacity duration-150 whitespace-nowrap bg-black/90 border border-white/10 px-2 py-1 rounded pointer-events-none ${editMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      >
        {project.name}{editMode ? ` · ${x.toFixed(1)},${y.toFixed(1)} · ${Math.round(facing)}°` : ""}
      </span>
    </motion.button>
  );
}

// While a launched project's tab is open, its ship flies a patrol orbit around the
// carrier instead of sitting in its bay - "revolving" literally means "this project is
// open right now". The orbit rides an ellipse just outside the docking ring; rotation
// stays tangential (nose into the direction of travel) and scale breathes with the same
// near/far depth as the ring. It starts at the angle nearest the ship's own bay, so the
// departure reads as "lifted off and swung into orbit right from home".
const ORBIT_RX = 46;
const ORBIT_RY = 37;
const ORBIT_STEPS = 48;
const ORBIT_TRANSITION = { duration: 34, ease: "linear", repeat: Infinity };
function buildOrbitKeyframes(bayX, bayY) {
  const startA = Math.atan2((bayY - RING_CY) / ORBIT_RY, (bayX - RING_CX) / ORBIT_RX);
  const xs = [], ys = [], rots = [], scales = [];
  let prevRot = null;
  for (let i = 0; i <= ORBIT_STEPS; i++) {
    const a = startA + (i / ORBIT_STEPS) * Math.PI * 2;
    xs.push(`${RING_CX + ORBIT_RX * Math.cos(a)}%`);
    ys.push(`${RING_CY + ORBIT_RY * Math.sin(a)}%`);
    const dx = -ORBIT_RX * Math.sin(a) * CONTAINER_ASPECT;
    const dy = ORBIT_RY * Math.cos(a);
    let rot = (Math.atan2(dx, -dy) * 180) / Math.PI;
    while (prevRot != null && rot < prevRot) rot += 360;
    prevRot = rot;
    rots.push(rot);
    scales.push(0.72 + ((Math.sin(a) + 1) / 2) * 0.5);
  }
  return { xs, ys, rots, scales };
}

function OrbitingShip({ project, layout, onFocus }) {
  const sizePct = SHIP_BASE_PCT;
  const { xs, ys, rots, scales } = useMemo(() => buildOrbitKeyframes(layout.x, layout.y), [layout.x, layout.y]);
  return (
    <motion.button
      data-testid={`ship-voyage-${project.id}`}
      onClick={(e) => { e.stopPropagation(); onFocus(project); }}
      title={`${project.name} is open in another tab - click to jump to it`}
      className="group absolute pointer-events-auto"
      style={{ width: `${sizePct}%`, aspectRatio: "1 / 1", marginLeft: `-${sizePct / 2}%`, marginTop: `-${sizePct / 2}%` }}
      initial={{ opacity: 0, left: xs[0], top: ys[0] }}
      animate={{ opacity: 1, left: xs, top: ys, scale: scales }}
      transition={{ left: ORBIT_TRANSITION, top: ORBIT_TRANSITION, scale: ORBIT_TRANSITION, opacity: { duration: 0.8 } }}
    >
      <motion.div
        className="relative w-full h-full"
        style={{ filter: "drop-shadow(0 3px 7px rgba(0,0,0,0.95)) drop-shadow(0 0 14px rgba(0,240,255,0.4)) brightness(1.06) contrast(1.08)" }}
        animate={{ rotate: rots }}
        transition={{ rotate: ORBIT_TRANSITION }}
      >
        <img
          src={layout.shipImg}
          alt={`${project.name} on patrol`}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.35]"
          draggable={false}
        />
        {/* Main engines burning hard - this ship is under way, not idling on a pad. */}
        <motion.span
          className="absolute pointer-events-none rounded-full"
          style={{
            left: "32%", width: "36%", bottom: "-4%", height: "22%",
            background: `radial-gradient(ellipse at center, rgba(255,255,255,0.95) 0%, ${layout.color}88 50%, transparent 78%)`,
            filter: "blur(2.5px)",
          }}
          animate={{ opacity: [0.7, 1, 0.7], scaleY: [1.5, 1.9, 1.5] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      <span
        data-testid={`voyage-label-${project.id}`}
        className="absolute left-1/2 top-full -translate-x-1/2 mt-1 text-[9px] font-mono-data uppercase tracking-wider text-white transition-opacity duration-150 whitespace-nowrap bg-black/90 border border-white/10 px-2 py-1 rounded pointer-events-none opacity-0 group-hover:opacity-100"
      >
        {project.name} · in flight - click to view
      </span>
    </motion.button>
  );
}

function ArchitectureLabel({ id, item, pos, delay, editMode, onDragStart, isDragging }) {
  return (
    <motion.div
      data-testid={`text-element-${id}`}
      onPointerDown={editMode ? (e) => onDragStart(e, id) : undefined}
      className={`absolute hidden sm:flex items-center gap-2 ${editMode ? "cursor-grab active:cursor-grabbing touch-none" : ""}`}
      style={{ top: `${pos.y}%`, left: `${pos.x}%`, transform: "translate(-50%, -50%)", zIndex: 7 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.6 }}
    >
      {editMode && (
        <motion.span
          className="absolute inset-0 rounded-lg border border-dashed -m-2"
          style={{ borderColor: isDragging ? "#FFD23F" : "rgba(0,240,255,0.5)" }}
          animate={{ opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        />
      )}
      <motion.span
        className="w-1.5 h-1.5 rounded-full bg-cyan-400"
        animate={{ scale: [1, 1.6, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2.2, repeat: Infinity }}
      />
      <div className="leading-tight">
        <p className="text-[9px] font-mono-data uppercase tracking-[0.15em] text-cyan-300">{item.label}</p>
        <p className="text-[8px] font-mono-data text-slate-500">{item.sub}</p>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [textLayout, setTextLayout] = useState({});
  const [booted, setBooted] = useState(false);
  // Keyed by project.id so multiple ships can launch independently - clicking a second
  // ship never cuts off the first one's still-in-flight animation.
  const [launchingShips, setLaunchingShips] = useState({});
  // Ships currently "on a voyage" - their project tab is open, so they orbit the carrier
  // instead of sitting in their bay. Window handles live in a ref (not state) and are
  // polled every second; when Doc closes the tab, the ship re-docks automatically.
  const [voyages, setVoyages] = useState({});
  const voyageWinsRef = useRef({});
  const voyageStartRef = useRef({});
  const pageVisibleSinceRef = useRef(Date.now());
  const [fleetHealth, setFleetHealth] = useState({});
  const [tourIndex, setTourIndex] = useState(-1); // -1 = bay tour inactive
  const [landingShips, setLandingShips] = useState({}); // ships mid docking-ceremony
  const [launchPulseKey, setLaunchPulseKey] = useState(0);
  const [showSubtitle, setShowSubtitle] = useState(false);
  // One-shot cinematic arrival: false while the carrier is still flying in from deep
  // space; true once it has settled at its final (zoomed-out) parking scale forever.
  const [arrivalDone, setArrivalDone] = useState(false);
  // Touchdown tremor: a 0.7s decaying camera shake the instant she parks.
  const [touchdownShake, setTouchdownShake] = useState(false);
  const { isAuthenticated, isAdmin, loading: authLoading, setShowLoginModal } = useAuth();
  const motherRef = useRef(null);

  // Drag-to-Dock calibration mode: admin drags each spacecraft into its visual pocket,
  // then "Lock Positions" permanently writes those exact coordinates to every project
  // document so the whole fleet stays perfectly seated for every visitor, forever.
  const [editMode, setEditMode] = useState(false);
  const [draftPositions, setDraftPositions] = useState({});
  const [dragId, setDragId] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);
  const [savingTextLayout, setSavingTextLayout] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    api.get("/projects").then((res) => {
      // Only show fully-docked ships on the mothership. Projects still in "awaiting"
      // (auto-discovered from GitHub but not yet docked, e.g. AVA-platform itself) are
      // hidden from the fleet view - they stay in the registry, just not rendered here.
      const visible = (res.data || []).filter((p) => p.status !== "awaiting");
      setProjects(visible);
      // Pre-warm every docked project's server the moment the mothership loads - many are
      // hosted on infra that spins down when idle ("waking up the servers..."). Pinging them
      // now, well before Doc ever clicks a ship, gives them a head start so launches feel
      // instant instead of sitting on a cold-start screen after the fact.
      visible.forEach((p) => {
        if (p.status === "docked" && p.url) {
          fetch(p.url, { mode: "no-cors", cache: "no-store" }).catch(() => {});
        }
      });
    }).catch(() => {});
    api.get("/layout/text").then((res) => setTextLayout(res.data || {})).catch(() => {});
  }, []);

  // Engine audio rides the WHOLE approach and stops exactly when she's parked out
  // front - not a moment before.
  useEffect(() => {
    if (booted) playArrivalEngine(ARRIVAL_MS);
  }, [booted]);

  // Fleet SSO: a ship bounced Doc here to authenticate (?sso_return=...). Once signed
  // in (Google or password), resume the handshake so the ship gets its identity token.
  useEffect(() => {
    const ssoReturn = new URLSearchParams(window.location.search).get("sso_return");
    if (!ssoReturn) return;
    if (isAuthenticated) window.location.replace(ssoReturn);
    else if (!authLoading) setShowLoginModal(true);
  }, [isAuthenticated, authLoading, setShowLoginModal]);

  // OCTON deep-link (?src=octon&scan_id=..&severity=..): stash the scan context so the
  // voice orb greets with it on the next session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("src") !== "octon") return;
    sessionStorage.setItem("ava_octon_deeplink", JSON.stringify({
      scan_id: params.get("scan_id") || "",
      severity: params.get("severity") || "",
    }));
  }, []);

  // Let AVA's global Voice Orb trigger a real, cinematic undock from anywhere it's mounted -
  // "open OCTON Retina" plays the exact same whoosh + liftoff as clicking the ship by hand.
  // Voice-initiated launches pass viaVoice:true so handleLaunch uses same-tab navigation
  // (a WebRTC data-channel event is NOT a user gesture, so window.open is popup-blocked).
  useEffect(() => {
    landingLaunchBridge.current = (project, options = {}) => {
      const idx = projects.findIndex((p) => p.id === project.id);
      const layout = effectivePosition(project, Math.max(idx, 0), projects.length || 1);
      handleLaunch(project, layout, options);
    };
    return () => { landingLaunchBridge.current = null; };
  });

  // Fleet-status bridge: lets AVA's voice orb answer "which ships are out?" with the
  // real live voyage list (projects launched from here whose tabs are still open).
  useEffect(() => {
    voyageStatusBridge.current = () =>
      projects.filter((p) => voyages[p.id]).map((p) => ({ name: p.name, url: p.url }));
    return () => { voyageStatusBridge.current = null; };
  }, [projects, voyages]);

  const handleDock = () => {
    if (isAdmin) navigate("/os");
    else if (isAuthenticated) navigate("/");
    else setShowLoginModal(true);
  };

  const handleLaunch = (project, layout, options = {}) => {
    if (!isAuthenticated) { setShowLoginModal(true); return; }
    if (launchingShips[project.id]) return; // already mid-flight - ignore a repeat/double click
    playWhooshSound();
    setLaunchPulseKey((k) => k + 1);
    // Graceful voice handoff: AVA announces "handing off to [Project]'s own voice"
    // out loud, then the orb disconnects itself a few seconds later - no more abrupt
    // severing mid-sentence. Falls back to the immediate disconnect when no live
    // session exists. Voice-initiated launches skip this entirely: the orb's own
    // launch_project tool response already carries the handoff announcement.
    if (!options.viaVoice) {
      try {
        const announced = voiceHandoffBridge.current && voiceHandoffBridge.current(project.name);
        if (!announced) { voiceDisconnectBridge.current && voiceDisconnectBridge.current(); }
      } catch (_) { /* voice never engaged this session - nothing to hand off */ }
    }
    // Undock: a realistic straight-up liftoff off the pad - no countdown, no sideways
    // sliding, just the whoosh and the shuttle ascending into the sky.
    setLaunchingShips((prev) => ({ ...prev, [project.id]: true }));
    // Open the destination - path depends on WHO initiated the launch:
    //   • Click / tap (user gesture)  → window.open(_blank), we get a tab handle
    //     and can watch it for close-events so the ship re-docks when the tab closes.
    //   • Voice command (async WebRTC callback, NOT a user gesture) → browsers
    //     silently block window.open, which was Doc's exact bug: the whoosh played
    //     and the animation ran but no tab opened. We navigate the current tab
    //     via window.location.href AFTER the animation, which is always allowed.
    if (project.status === "docked" && project.url) {
      if (options.viaVoice) {
        // The orb schedules its own guaranteed-fire navigation timer BEFORE calling
        // this bridge, so Landing no longer schedules a redundant one (which under
        // some React-teardown timings on Doc's laptop was being orphaned). We just
        // play the animation and let the orb-owned timer handle window.location.
        if (!options.skipNavigation) {
          setTimeout(() => { window.location.href = project.url; }, 950);
        }
      } else {
        // No "noopener" here on purpose: we need the window handle to know when Doc closes
        // the project tab so the ship can leave orbit and re-dock. (noopener also made
        // window.open return null, falsely tripping the pop-up-blocked toast.) These are
        // Doc's own trusted OCTON apps, so keeping the opener link is fine.
        const win = window.open(project.url, "_blank");
        if (!win) {
          toast.error(`Pop-up blocked - allow pop-ups for this site, then click ${project.name} again.`);
        } else {
          voyageWinsRef.current[project.id] = win;
        }
      }
    }
    setTimeout(() => {
      if (!(project.status === "docked" && project.url)) {
        navigate("/os", { state: { openDockingBay: true, focusProjectId: project.id } });
      }
      setLaunchingShips((prev) => {
        const next = { ...prev };
        delete next[project.id];
        return next;
      });
      // Liftoff complete - if the project tab is still open, the ship enters its patrol
      // orbit around the carrier (revolving = "this project is open right now").
      // Voice-initiated launches leave this tab entirely (window.location.href fires
      // just before this callback), so we skip the orbit setup - there's no window
      // handle to track and the mothership page itself is unmounting.
      if (options.viaVoice) return;
      const win = voyageWinsRef.current[project.id];
      if (win && !win.closed) {
        voyageStartRef.current[project.id] = Date.now();
        setVoyages((prev) => ({ ...prev, [project.id]: true }));
      } else {
        delete voyageWinsRef.current[project.id];
      }
    }, 1050);
  };

  // Track when the mothership page (re)gains visibility - a returning Doc gets a short
  // ceremonial lap from any orbiting ship before it auto-docks itself.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") pageVisibleSinceRef.current = Date.now();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Watch every open voyage tab - a ship re-docks the moment Doc closes its project's
  // tab, OR (Doc's rule) docks itself after completing a full patrol lap while he's
  // actually here watching the mothership: no ship circles forever while you're home.
  useEffect(() => {
    const ids = Object.keys(voyages);
    if (!ids.length) return undefined;
    const dockShipHome = (id) => {
      delete voyageWinsRef.current[id];
      delete voyageStartRef.current[id];
      setVoyages((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // Docking ceremony: touchdown chime + descent animation as the ship re-docks.
      playDockingChime();
      setLandingShips((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setLandingShips((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, 1700);
    };
    const timer = setInterval(() => {
      ids.forEach((id) => {
        const win = voyageWinsRef.current[id];
        if (!win || win.closed) {
          dockShipHome(id);
          return;
        }
        // Auto-dock after one full orbit (34s) - but only while the AVA page has been
        // visible a few seconds, so returning mid-lap still shows a short ceremonial
        // circle before touchdown. Tab hidden = keep patrolling.
        const startedAt = voyageStartRef.current[id] || Date.now();
        const visibleFor = document.visibilityState === "visible"
          ? Date.now() - pageVisibleSinceRef.current : 0;
        if (Date.now() - startedAt >= 34000 && visibleFor >= 5000) dockShipHome(id);
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [voyages]);

  // Live fleet health: poll the backend's ping results every minute.
  useEffect(() => {
    const load = () => api.get("/projects/health").then((r) => setFleetHealth(r.data || {})).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  // Bay tour bridge: AVA's fleet_tour voice tool starts the spotlight sweep from here and
  // gets back the ordered roster to narrate in sync.
  useEffect(() => {
    bayTourBridge.current = () => {
      setTourIndex(0);
      return projects.map((p, i) => ({
        order: i + 1,
        name: p.name,
        status: voyages[p.id] ? "in flight" : (fleetHealth[p.id] || {}).status === "down" ? "docked - app DOWN" : "docked",
      }));
    };
    window.__avaStartBayTour = bayTourBridge.current;
    return () => { bayTourBridge.current = null; window.__avaStartBayTour = null; };
  }, [projects, voyages, fleetHealth]);

  // Advance the tour spotlight bay to bay (~2.5s per ship), then fade out.
  useEffect(() => {
    if (tourIndex < 0) return undefined;
    if (tourIndex >= projects.length) {
      setTourIndex(-1);
      return undefined;
    }
    const timer = setTimeout(() => setTourIndex((i) => i + 1), 2500);
    return () => clearTimeout(timer);
  }, [tourIndex, projects.length]);

  const handleFocusVoyage = (project) => {
    const win = voyageWinsRef.current[project.id];
    if (win && !win.closed) {
      try { win.focus(); } catch { /* browser refused focus steal - harmless */ }
    }
  };

  const handleDragStart = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    const idx = projects.findIndex((pr) => pr.id === project.id);
    const current = draftPositions[project.id] || getShipLayout(project, Math.max(idx, 0), projects.length || 1);
    dragRef.current = {
      id: project.id,
      mode: "move",
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: current.x,
      startY: current.y,
    };
    setDragId(project.id);
  };

  const handleRotateStart = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    const idx = projects.findIndex((pr) => pr.id === project.id);
    const current = draftPositions[project.id] || getShipLayout(project, Math.max(idx, 0), projects.length || 1);
    dragRef.current = {
      id: project.id,
      mode: "rotate",
      startX: current.x,
      startY: current.y,
    };
    setDragId(project.id);
  };

  const handleTextDragStart = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const current = draftPositions[id] || textLayout[id] || TEXT_ELEMENT_DEFAULTS[id];
    dragRef.current = {
      id,
      mode: "move",
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: current.x,
      startY: current.y,
    };
    setDraftPositions((prev) => ({ ...prev, [id]: current }));
    setDragId(id);
  };

  const effectiveTextPos = (id) => draftPositions[id] || textLayout[id] || TEXT_ELEMENT_DEFAULTS[id];

  useEffect(() => {
    if (!dragId) return;
    const onMove = (e) => {
      const d = dragRef.current;
      const rect = motherRef.current?.getBoundingClientRect();
      if (!d || !rect) return;
      if (d.mode === "rotate") {
        const centerPxX = rect.left + (d.startX / 100) * rect.width;
        const centerPxY = rect.top + (d.startY / 100) * rect.height;
        const angle = (Math.atan2(e.clientY - centerPxY, e.clientX - centerPxX) * 180) / Math.PI;
        const facing = (angle + 90 + 360) % 360;
        setDraftPositions((prev) => ({ ...prev, [d.id]: { ...prev[d.id], x: d.startX, y: d.startY, facing } }));
        return;
      }
      const deltaXPct = ((e.clientX - d.startClientX) / rect.width) * 100;
      const deltaYPct = ((e.clientY - d.startClientY) / rect.height) * 100;
      const nextX = Math.min(98, Math.max(2, d.startX + deltaXPct));
      const nextY = Math.min(98, Math.max(2, d.startY + deltaYPct));
      setDraftPositions((prev) => ({ ...prev, [d.id]: { ...prev[d.id], x: nextX, y: nextY } }));
    };
    const onUp = () => {
      dragRef.current = null;
      setDragId(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragId]);

  // Slot assignment that NEVER moves an existing locked ship: locked ships (dock_x/dock_y
  // stored server-side) render at their exact saved coordinates on every device, while any
  // UNLOCKED ship claims the vacant left-wing bays in order (then overflows onto the ring)
  // - so a newly docked project takes a spare pad instead of colliding with a locked bay.
  const lockedIdSet = new Set(projects.filter((p) => p.dock_x != null && p.dock_y != null).map((p) => p.id));
  const unlockedIdOrder = projects.filter((p) => !lockedIdSet.has(p.id)).map((p) => p.id);
  // A vacant pad only counts as free if no locked ship already sits on/near it - Doc
  // hand-locked ships onto some of these pads, so auto-seating must skip those.
  const lockedSpots = projects.filter((p) => lockedIdSet.has(p.id));
  const freeBays = VACANT_BAYS.filter(
    (pad) => !lockedSpots.some((p) => Math.abs(p.dock_x - pad.x) < 4 && Math.abs(p.dock_y - pad.y) < 5)
  );
  const effectivePosition = (project, index, total) => {
    const slotOrdinal = lockedIdSet.has(project.id)
      ? index
      : unlockedIdOrder.indexOf(project.id);
    const draft = draftPositions[project.id];
    const base = getShipLayout(project, slotOrdinal, total, freeBays);
    if (!draft) return base;
    return { ...base, x: draft.x ?? base.x, y: draft.y ?? base.y, facing: draft.facing ?? base.facing };
  };


  const handleLockPositions = async () => {
    setSavingLayout(true);
    const total = projects.length || 1;
    const positions = projects.map((p, i) => {
      const layout = effectivePosition(p, i, total);
      return { id: p.id, x: layout.x, y: layout.y, facing: layout.facing, depth_scale: layout.depthScale };
    });
    const textElements = Object.keys(TEXT_ELEMENT_DEFAULTS).reduce((acc, id) => {
      const pos = effectiveTextPos(id);
      acc[id] = { x: pos.x, y: pos.y };
      return acc;
    }, {});
    try {
      const [projRes, textRes] = await Promise.all([
        api.post("/projects/layout/lock", { positions }),
        api.post("/layout/text/lock", { elements: textElements }),
      ]);
      setProjects(projRes.data);
      setTextLayout(textRes.data);
      setDraftPositions({});
      setEditMode(false);
      toast.success("Fleet + heading positions locked - every visitor sees this exact layout now.");
    } catch (err) {
      toast.error("Failed to lock positions. Try again.");
    } finally {
      setSavingLayout(false);
    }
  };

  const handleCancelEdit = () => {
    setDraftPositions({});
    setEditMode(false);
  };

  // Dedicated text-only lock: lets Doc secure the AVA heading/label placement
  // independently of the fleet, with its own explicit confirmation - no more guessing
  // whether the text got saved along with everything else.
  const handleLockTextPositions = async () => {
    setSavingTextLayout(true);
    const textElements = Object.keys(TEXT_ELEMENT_DEFAULTS).reduce((acc, id) => {
      const pos = effectiveTextPos(id);
      acc[id] = { x: pos.x, y: pos.y };
      return acc;
    }, {});
    try {
      const textRes = await api.post("/layout/text/lock", { elements: textElements });
      setTextLayout(textRes.data);
      setDraftPositions((prev) => {
        const next = { ...prev };
        Object.keys(TEXT_ELEMENT_DEFAULTS).forEach((id) => delete next[id]);
        return next;
      });
      toast.success("AVA heading + labels locked - text positions saved.");
    } catch (err) {
      toast.error("Failed to lock text positions. Try again.");
    } finally {
      setSavingTextLayout(false);
    }
  };

  const handleResetLayout = async () => {
    if (!window.confirm("Reset the ENTIRE fleet + heading layout back to the auto-computed default? This permanently discards every manually-locked position.")) {
      return;
    }
    setSavingLayout(true);
    try {
      const [projRes, textRes] = await Promise.all([
        api.post("/projects/layout/reset"),
        api.post("/layout/text/reset"),
      ]);
      setProjects(projRes.data);
      setTextLayout(textRes.data);
      setDraftPositions({});
      toast.success("Reverted to auto-computed layout.");
    } catch (err) {
      toast.error("Failed to reset layout.");
    } finally {
      setSavingLayout(false);
    }
  };

  return (
    <div className={`relative min-h-screen w-full overflow-hidden bg-[#030509] ${touchdownShake ? "ava-shake" : ""}`}>
      <BootSequence onComplete={() => setBooted(true)} />
      {!booted && <div className="fixed inset-0 z-[90] bg-[#030509]" />}
      {!authLoading && !isAuthenticated && !IS_TOUCH_DEVICE && <LandingVoiceGreeter />}

      <Starfield count={160} />
      {booted && !arrivalDone && <ArrivalWarp duration={ARRIVAL_MS} cx={0.5} cy={0.45} />}
      <div className="absolute inset-0 grid-texture opacity-60" />
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: `radial-gradient(circle at 50% 38%, rgba(0,240,255,0.08), transparent 55%)` }}
      />

      <motion.nav
        initial={{ opacity: 0, y: -10 }}
        animate={booted ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.2 }}
        className="relative z-20 flex items-center justify-between px-5 sm:px-10 py-3"
      >
        <div className="flex items-center gap-3">
          <img src={EMBLEM_URL} alt="OCTON AVA" className="w-9 h-9 rounded-lg" data-testid="ava-emblem" />
          <span className="font-display font-bold tracking-tight text-lg text-white">OCTON <span className="text-cyan-400">AVA</span></span>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="download-ava-button"
            onClick={() => setShowDownloadModal(true)}
            title="Get AVA on your phone"
            className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:bg-white/5 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors duration-200"
          >
            <Smartphone className="w-3 h-3" /> Download
          </button>
          {isAuthenticated && (
            <button
              data-testid="edit-layout-toggle"
              onClick={() => (editMode ? handleCancelEdit() : setEditMode(true))}
              className={`text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors duration-200 ${
                editMode
                  ? "bg-amber-400/15 border-amber-400/60 text-amber-300"
                  : "bg-cyan-400/5 border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/15"
              }`}
            >
              {editMode ? "✕ Exit Edit Mode" : "⚙ Edit Layout"}
            </button>
          )}
        </div>
      </motion.nav>

      <AnimatePresence>
        {editMode && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            data-testid="edit-layout-toolbar"
            className="relative z-20 mx-auto -mt-1 mb-4 flex w-fit items-center gap-3 rounded-full border border-amber-400/40 bg-black/70 px-4 py-2 backdrop-blur-sm"
          >
            <span className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-amber-300">
              Drag ships/text to move · yellow nose-handle to rotate
            </span>
            <button
              data-testid="lock-positions-button"
              onClick={handleLockPositions}
              disabled={savingLayout}
              className="text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50 transition-colors"
            >
              {savingLayout ? "Locking..." : "Lock Positions"}
            </button>
            <button
              data-testid="lock-text-positions-button"
              onClick={handleLockTextPositions}
              disabled={savingTextLayout}
              title="Save ONLY the AVA heading + label placement, independent of the fleet"
              className="text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/15 disabled:opacity-50 transition-colors"
            >
              {savingTextLayout ? "Locking Text..." : "Lock Text Positions"}
            </button>
            <button
              data-testid="reset-layout-button"
              onClick={handleResetLayout}
              disabled={savingLayout}
              className="text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-slate-500 text-slate-300 hover:border-slate-300 disabled:opacity-50 transition-colors"
            >
              Reset All
            </button>
            <button
              data-testid="cancel-edit-layout-button"
              onClick={handleCancelEdit}
              className="text-[10px] font-mono-data uppercase tracking-[0.2em] px-3 py-1 rounded-full text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={booted ? { opacity: 1 } : {}}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="relative z-10 flex flex-col items-center px-6 pb-16"
      >
        <AnimatePresence>
          {showSubtitle && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: "auto", marginBottom: 40 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden text-center"
              data-testid="landing-subtitle"
            >
              <p className="text-[11px] font-mono-data uppercase tracking-[0.35em] text-amber-400 mb-3">
                Living Super-Brain Operating System
              </p>
              <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                Every OCTON project docks here as a ship. AVA is the cognitive core -
                Hebbian memory, Bayesian reasoning, one living organism commanding the fleet.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative w-full max-w-[1500px] aspect-[3/2]" style={{ perspective: 1400 }}>
          {/* Distant engine light: far ships read as LIGHTS before hulls - a bright point
              bloom marks the approaching carrier while it's still a speck, swelling and
              then dissolving exactly as the hull resolves to take its place. */}
          {booted && !arrivalDone && (
            <motion.div
              className="absolute pointer-events-none rounded-full"
              data-testid="arrival-engine-light"
              style={{
                left: "50%", top: "43%", width: 130, height: 130,
                marginLeft: -65, marginTop: -65, zIndex: 2,
                background: "radial-gradient(circle, rgba(235,252,255,0.95) 0%, rgba(120,220,255,0.4) 32%, transparent 68%)",
                filter: "blur(2px)",
              }}
              initial={{ opacity: 0, scale: 0.3 }}
              animate={{ opacity: [0, 0.9, 0.7, 0], scale: [0.3, 0.5, 1.5, 2.6] }}
              transition={{ duration: ARRIVAL_MS / 1000, times: [0, 0.2, 0.55, 0.82], ease: "easeInOut" }}
            />
          )}
          <motion.div
          ref={motherRef}
          className="relative w-full h-full cursor-pointer"
          data-testid="mothership-visual"
          onClick={editMode ? undefined : handleDock}
          title={editMode ? "Drag-to-Dock calibration mode" : isAuthenticated ? "Enter AVA OS" : "Click to authenticate"}
          // Cinematic arrival - Star Wars opening in REVERSE: the carrier drops out of
          // warp as a distant light, flies AT the viewer along one continuous perspective
          // curve (approachEase) with a slight approach vector from upper-left and a
          // levelling-off 3D pitch (rotateX 22°→0 under the wrapper's perspective), and
          // decelerates smoothly into its final zoomed-out parking scale. One-shot.
          initial={{ scale: ARRIVAL_START_SCALE, x: "-5%", y: "-14%", rotateX: 22, opacity: 0 }}
          animate={
            !booted
              ? {}
              : arrivalDone
                ? { scale: [ARRIVAL_FINAL_SCALE, ARRIVAL_FINAL_SCALE + 0.012, ARRIVAL_FINAL_SCALE], x: "0%", y: "0%", rotateX: 0, opacity: 1 }
                : { scale: ARRIVAL_FINAL_SCALE, x: "0%", y: "0%", rotateX: 0, opacity: 1 }
          }
          transition={
            arrivalDone
              ? { scale: { duration: 6, repeat: Infinity, ease: "easeInOut" }, default: { duration: 0.3 } }
              : {
                  scale: { duration: ARRIVAL_MS / 1000, ease: approachEase },
                  x: { duration: ARRIVAL_MS / 1000, ease: "easeOut" },
                  y: { duration: ARRIVAL_MS / 1000, ease: "easeOut" },
                  rotateX: { duration: ARRIVAL_MS / 1000, ease: "easeOut" },
                  opacity: { duration: 1.4, ease: "easeOut" },
                }
          }
          onAnimationComplete={() => {
            if (booted && !arrivalDone) {
              setArrivalDone(true);
              setTouchdownShake(true);
              setTimeout(() => setTouchdownShake(false), 750);
            }
          }}
        >
          <div className="absolute inset-0 overflow-hidden">
            {/* Feather the mothership photo's own edges into the deep-space background via a
                soft radial CSS mask - kills the visible rectangular hard-cut of the original
                artwork so the carrier truly appears to be floating in open space instead of
                sitting inside a framed picture. The mask ellipse is deliberately wider than
                tall (75% x 62%) to match the 3:2 container aspect, and starts feathering
                gradually at 30% out so the ship's silhouette stays crisp while its outer
                halo - especially the tall atmospheric glow beam above the dome - dissolves
                cleanly into the starfield without any horizontal cutoff line at the top. */}
            <img
              src={MOTHERSHIP_URL}
              alt="AVA Mothership Architecture"
              className="w-full h-full object-cover"
              style={{
                WebkitMaskImage: "radial-gradient(ellipse 75% 62% at 50% 55%, #000 30%, rgba(0,0,0,0.8) 58%, rgba(0,0,0,0.3) 82%, transparent 100%)",
                maskImage: "radial-gradient(ellipse 75% 62% at 50% 55%, #000 30%, rgba(0,0,0,0.8) 58%, rgba(0,0,0,0.3) 82%, transparent 100%)",
              }}
            />
            {/* Subtle vignette pulling the extreme corners toward pure background - belt-and-
                braces with the image mask above; the two together guarantee zero visible
                seam even on wide monitors where the corners are furthest from centre. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse 78% 68% at 50% 55%, transparent 50%, rgba(3,5,9,0.7) 85%, #030509 100%)" }}
            />
            {/* Extra top-edge linear fade - the atmospheric glow beam extends higher than the
                radial ellipse's soft-edge, so we layer a linear top-to-bottom fade that pulls
                the upper ~30% of the container gently back to the deep-space colour. This is
                what erases the last faint horizontal cutoff line above the carrier's dome. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(to bottom, #030509 0%, rgba(3,5,9,0.7) 8%, rgba(3,5,9,0.25) 20%, transparent 34%)" }}
            />
            {/* Mothership illuminated from inside - same ice-teal light bleeding through the
                hull as the docking bay pockets baked into the photo, breathing gently so the
                whole dome feels alive rather than a bolted-on ring overlay. */}
            <motion.div
              data-testid="mothership-hull-glow"
              className="absolute pointer-events-none mix-blend-screen"
              style={{
                left: `${DOME_CX}%`,
                top: `${DOME_CY}%`,
                width: "44%",
                height: "30%",
                transform: "translate(-50%, -50%)",
                backgroundImage: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(224,247,255,0.5), rgba(120,220,255,0.16) 48%, transparent 74%)",
                filter: "blur(5px)",
              }}
              animate={{ opacity: [0.45, 0.8, 0.45] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Mothership acknowledges a launch: the hull flares brighter from within, as if
                the mother knows a child is taking off. */}
            <motion.div
              key={`dome-flash-${launchPulseKey}`}
              data-testid="mothership-hull-flare"
              className="absolute pointer-events-none mix-blend-screen"
              style={{
                left: `${DOME_CX}%`,
                top: `${DOME_CY}%`,
                width: "52%",
                height: "36%",
                transform: "translate(-50%, -50%)",
                backgroundImage: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(234,252,255,0.95), rgba(150,230,255,0.35) 50%, transparent 76%)",
                filter: "blur(3px)",
              }}
              initial={{ opacity: 0 }}
              animate={launchPulseKey > 0 ? { opacity: [0, 1, 0], scale: [1, 1.15, 1.05] } : { opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
            />
          </div>

          {ARCHITECTURE_LABELS.map((item, i) => {
            const id = `label-${i}`;
            return (
              <ArchitectureLabel
                key={id}
                id={id}
                item={item}
                pos={effectiveTextPos(id)}
                delay={1.2 + i * 0.2}
                editMode={editMode}
                onDragStart={handleTextDragStart}
                isDragging={dragId === id}
              />
            );
          })}

          <motion.h1
            onPointerDown={editMode ? (e) => handleTextDragStart(e, "ava-heading") : undefined}
            className={`absolute flex flex-col items-center select-none ${editMode ? "cursor-grab active:cursor-grabbing touch-none" : "cursor-pointer"}`}
            style={{ top: `${effectiveTextPos("ava-heading").y}%`, left: `${effectiveTextPos("ava-heading").x}%`, transform: "translate(-50%, -50%)", zIndex: 7 }}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            onClick={editMode ? (e) => e.stopPropagation() : (e) => { e.stopPropagation(); handleDock(); }}
            onMouseEnter={() => setShowSubtitle(true)}
            onMouseLeave={() => setShowSubtitle(false)}
            title={editMode ? "Drag to reposition the AVA heading" : isAuthenticated ? "Enter AVA OS" : "Click to authenticate"}
            data-testid="landing-title"
          >
            {editMode && (
              <motion.span
                className="absolute inset-0 rounded-lg border border-dashed -m-2"
                style={{ borderColor: dragId === "ava-heading" ? "#FFD23F" : "rgba(0,240,255,0.5)" }}
                animate={{ opacity: [0.5, 0.9, 0.5] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
            )}
            <span
              className="font-alien font-medium text-sm sm:text-base tracking-[0.4em] text-cyan-200 uppercase hover:text-cyan-100 transition-colors duration-300"
              style={{ textShadow: "0 0 12px rgba(0,240,255,0.7), 0 0 24px rgba(0,240,255,0.35)" }}
            >
              AVA
            </span>
          </motion.h1>

          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 5, pointerEvents: "none" }}>
            <ellipse cx={`${RING_CX}%`} cy={`${RING_CY}%`} rx={`${RING_RX}%`} ry={`${RING_RY}%`} fill="none" stroke="rgba(0,240,255,0.05)" strokeWidth="10" strokeDasharray="0" />
            {projects.map((p, i) => {
              const layout = effectivePosition(p, i, projects.length || 1);
              return <ShipConnector key={p.id} x={layout.x} y={layout.y} color={layout.color} />;
            })}
          </svg>

          {/* Orbital Docking Ring pads - occupied pads glow directly beneath each ship's
              actual (locked or auto) position; the vacant left-wing bays mirror the right
              wing's locked fleet arc exactly, so both wings read 10 bays each. Unlocked
              new arrivals seat into these left-wing bays first. */}
          <div className="absolute inset-0" style={{ zIndex: 4, pointerEvents: "none" }}>
            {projects.map((p, i) => {
              const layout = effectivePosition(p, i, projects.length || 1);
              return (
                <BayPad
                  key={`pad-occ-${p.id}`}
                  x={layout.x}
                  y={layout.y}
                  depthScale={layout.depthScale}
                  color={layout.color}
                  occupied
                  index={i}
                />
              );
            })}
            {freeBays.map((slot, j) => (
              <BayPad
                key={`pad-vac-${j}`}
                x={slot.x}
                y={slot.y}
                depthScale={slot.depthScale}
                color="#00F0FF"
                occupied={j < unlockedIdOrder.length}
                outer={!!slot.outer}
                index={(projects.length || 0) + j}
              />
            ))}
          </div>

          {/* Fleet Health Glow - a docked ship's socket flickers amber when AVA hasn't been
              able to sync fresh knowledge/commits from it in over 7 days, a quiet "haven't
              heard from this ship in a while" signal sitting right at its bay. */}
          <div className="absolute inset-0" style={{ zIndex: 4, pointerEvents: "none" }}>
            {projects.map((p, i) => {
              if (!isStaleSync(p)) return null;
              const layout = effectivePosition(p, i, projects.length || 1);
              return (
                <motion.div
                  key={`stale-${p.id}`}
                  data-testid={`fleet-health-glow-${p.id}`}
                  title={`${p.name}: no fresh sync in over 7 days`}
                  className="absolute rounded-full"
                  style={{
                    left: `${layout.x}%`, top: `${layout.y}%`, transform: "translate(-50%, -50%)",
                    width: 34, height: 34,
                    background: "radial-gradient(circle, rgba(255,159,28,0.55), rgba(255,159,28,0.12) 55%, transparent 75%)",
                    filter: "blur(2px)",
                  }}
                  animate={{ opacity: [0.25, 0.85, 0.25] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                />
              );
            })}
          </div>

          <div className="absolute inset-0" style={{ zIndex: 6, pointerEvents: "none" }}>
            {projects.map((p, i) => {
              if (voyages[p.id]) return null; // ship is out on a voyage - its bay sits empty
              const layout = effectivePosition(p, i, projects.length || 1);
              return (
                <ShipMarker
                  key={p.id}
                  project={p}
                  index={i}
                  {...layout}
                  onLaunch={handleLaunch}
                  editMode={editMode}
                  onDragStart={handleDragStart}
                  onRotateStart={handleRotateStart}
                  isDragging={dragId === p.id}
                  launching={!!launchingShips[p.id]}
                  landing={!!landingShips[p.id]}
                  health={(fleetHealth[p.id] || {}).status}
                />
              );
            })}
          </div>

          {/* Bay tour spotlight - glides bay to bay while AVA narrates the fleet. */}
          {tourIndex >= 0 && tourIndex < projects.length && (() => {
            const p = projects[tourIndex];
            const layout = effectivePosition(p, tourIndex, projects.length || 1);
            return (
              <motion.div
                className="absolute pointer-events-none"
                style={{
                  zIndex: 9,
                  width: `${SHIP_BASE_PCT * 2.2}%`,
                  aspectRatio: "1 / 1",
                  marginLeft: `-${SHIP_BASE_PCT * 1.1}%`,
                  marginTop: `-${SHIP_BASE_PCT * 1.1}%`,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, left: `${layout.x}%`, top: `${layout.y}%` }}
                transition={{ left: { type: "spring", stiffness: 80, damping: 17 }, top: { type: "spring", stiffness: 80, damping: 17 }, opacity: { duration: 0.4 } }}
                data-testid="bay-tour-spotlight"
              >
                <span className="absolute inset-0 rounded-full border-2 border-cyan-300/70 animate-ping" style={{ animationDuration: "1.6s" }} />
                <span className="absolute inset-0 rounded-full border border-cyan-300/50" style={{ boxShadow: "0 0 30px rgba(0,240,255,0.35), inset 0 0 22px rgba(0,240,255,0.15)" }} />
                <span className="absolute left-1/2 top-full -translate-x-1/2 mt-2 whitespace-nowrap text-[10px] font-mono-data uppercase tracking-widest text-cyan-200 bg-black/85 border border-cyan-400/30 px-2.5 py-1 rounded" data-testid="bay-tour-label">
                  {tourIndex + 1}/{projects.length} · {p.name}
                </span>
              </motion.div>
            );
          })()}

          {/* Ships on voyage - orbiting the carrier while their project tab stays open. */}
          <div className="absolute inset-0" style={{ zIndex: 7, pointerEvents: "none" }}>
            {projects.map((p, i) => {
              if (!voyages[p.id]) return null;
              const layout = effectivePosition(p, i, projects.length || 1);
              return (
                <OrbitingShip
                  key={`voyage-${p.id}`}
                  project={p}
                  layout={layout}
                  onFocus={handleFocusVoyage}
                />
              );
            })}
          </div>
        </motion.div>
        </div>

        <p className="mt-10 text-[10px] font-mono-data uppercase tracking-[0.25em] text-slate-600" data-testid="fleet-count">
          {projects.length} vessels registered in the fleet
        </p>

        <div className="mt-10 w-full max-w-4xl overflow-hidden border-t border-b border-white/5 py-3">
          <div className="marquee-track">
            {[...projects, ...projects].map((p, i) => (
              <span key={i} className="mx-5 text-[10px] font-mono-data uppercase tracking-wider text-slate-500 whitespace-nowrap">
                <span className="text-cyan-500/70">◆</span> {p.name}
              </span>
            ))}
          </div>
        </div>
      </motion.div>

      <DownloadModal open={showDownloadModal} onClose={() => setShowDownloadModal(false)} />
    </div>
  );
}
